import { useEffect, useRef, useState } from 'react'
import type { ThemeMode } from '../../domain/theme'
import { parseLeveleriaBackup, summarizeBackup, type LeveleriaBackup } from '../../domain/backup'
import { CheckIcon, CloseIcon, DeviceIcon, DownloadIcon, MoonIcon, SunIcon, UploadIcon } from './Icons'
import { APP_RELEASE_LABEL } from '../../domain/appVersion'
import { useI18n } from '../../i18n'
import { setAnalyticsEnabled, trackEvent, useAnalyticsPreference } from '../../analytics'

interface ThemeSheetProps {
  open: boolean
  selected: ThemeMode
  onClose: () => void
  onSelect: (mode: ThemeMode) => void
  saving: boolean
  onExport: () => Promise<LeveleriaBackup | null>
  onImport: (backup: LeveleriaBackup) => Promise<boolean>
}

export const ThemeSheet = ({ open, selected, onClose, onSelect, saving, onExport, onImport }: ThemeSheetProps) => {
  const { language, setLanguage, copy: { common, app, settings } } = useI18n()
  const analyticsEnabled = useAnalyticsPreference()
  const options: Array<{ mode: ThemeMode; label: string; description: string; Icon: typeof DeviceIcon }> = [
    { mode: 'system', label: settings.system, description: settings.systemHelp, Icon: DeviceIcon },
    { mode: 'dark', label: settings.dark, description: settings.darkHelp, Icon: MoonIcon },
    { mode: 'light', label: settings.light, description: settings.lightHelp, Icon: SunIcon },
  ]
  const dialogRef = useRef<HTMLDialogElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingBackup, setPendingBackup] = useState<LeveleriaBackup | null>(null)
  const [fileName, setFileName] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  const downloadBackup = async () => {
    setLocalError(null)
    setMessage(null)
    const backup = await onExport()
    if (!backup) {
      setLocalError(settings.backupFailed)
      return
    }
    const blob = new Blob([`${JSON.stringify(backup, null, 2)}\n`], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `leveleria-backup-${backup.exportedAt.slice(0, 10)}.json`
    document.body.append(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
    setMessage(settings.backupDone)
  }

  const selectBackup = async (file: File | undefined) => {
    setPendingBackup(null)
    setMessage(null)
    setLocalError(null)
    if (!file) return
    if (file.size > 20 * 1024 * 1024) {
      setLocalError(settings.tooLarge)
      return
    }
    try {
      const parsed: unknown = JSON.parse(await file.text())
      setPendingBackup(parseLeveleriaBackup(parsed))
      setFileName(file.name)
    } catch (cause) {
      setLocalError(language === 'es' && cause instanceof Error ? cause.message : settings.readFailed)
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const restoreBackup = async () => {
    if (!pendingBackup) return
    const imported = await onImport(pendingBackup)
    if (!imported) {
      setLocalError(settings.restoreFailed)
      return
    }
    setPendingBackup(null)
    setMessage(settings.restored)
  }

  const summary = pendingBackup ? summarizeBackup(pendingBackup) : null

  return (
    <dialog ref={dialogRef} className="sheet theme-sheet" onCancel={(event) => { event.preventDefault(); onClose() }} onClose={onClose}>
      <div className="sheet__body">
        <div className="sheet__handle" />
        <div className="sheet__heading">
          <div><h2>{settings.title}</h2><p>{settings.intro}</p></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={common.close}><CloseIcon /></button>
        </div>
        <div className="theme-options">
          {options.map(({ mode, label, description, Icon }) => (
            <button
              key={mode}
              className={selected === mode ? 'theme-option theme-option--selected' : 'theme-option'}
              type="button"
              aria-pressed={selected === mode}
              onClick={() => { onSelect(mode); onClose() }}
            >
              <span className="theme-option__icon"><Icon /></span>
              <span><strong>{label}</strong><small>{description}</small></span>
              {selected === mode && <CheckIcon className="theme-option__check" />}
            </button>
          ))}
        </div>
        <p className="eyebrow settings-section-title">{settings.language}</p>
        <div className="language-switch language-switch--settings" role="group" aria-label={settings.language}>
          <button className={language === 'en' ? 'language-switch__button language-switch__button--selected' : 'language-switch__button'} type="button" aria-pressed={language === 'en'} onClick={() => setLanguage('en')}>English</button>
          <button className={language === 'es' ? 'language-switch__button language-switch__button--selected' : 'language-switch__button'} type="button" aria-pressed={language === 'es'} onClick={() => setLanguage('es')}>Español</button>
        </div>
        <p className="settings-language-note">{settings.automaticNote}</p>
        <p className="eyebrow settings-section-title">{settings.privacy}</p>
        <label className="analytics-card">
          <span><strong>{settings.analytics}</strong><small>{settings.analyticsHelp}</small></span>
          <input type="checkbox" checked={analyticsEnabled} onChange={(event) => setAnalyticsEnabled(event.target.checked)} />
          <span className="analytics-toggle" aria-hidden="true"><i /></span>
          <em>{analyticsEnabled ? settings.analyticsOn : settings.analyticsOff}</em>
        </label>
        <p className="eyebrow settings-section-title">{settings.contact}</p>
        <section className="contact-card">
          <div><strong>{settings.contactTitle}</strong><p>{settings.contactHelp}</p></div>
          <a className="button button--outline" href="mailto:hello@leveleria.com" onClick={() => trackEvent('feedback_opened', language)}>{settings.email}</a>
        </section>
        <p className="eyebrow settings-section-title">{settings.data}</p>
        <section className="backup-card">
          <div>
            <strong>{settings.localBackup}</strong>
            <p>{settings.backupHelp}</p>
          </div>
          <div className="backup-actions">
            <button className="button button--outline" type="button" disabled={saving} onClick={downloadBackup}><DownloadIcon />{settings.export}</button>
            <button className="button button--outline" type="button" disabled={saving} onClick={() => fileInputRef.current?.click()}><UploadIcon />{settings.import}</button>
          </div>
          <input ref={fileInputRef} className="visually-hidden" type="file" accept="application/json,.json" onChange={(event) => void selectBackup(event.target.files?.[0])} />
          {message && <p className="backup-message" role="status"><CheckIcon />{message}</p>}
          {localError && <p className="error-message backup-error" role="alert">{localError}</p>}
        </section>
        {pendingBackup && summary && (
          <section className="backup-confirm" role="alertdialog" aria-labelledby="backup-confirm-title">
            <h3 id="backup-confirm-title">{settings.replaceTitle}</h3>
            <p><strong>{fileName}</strong> {settings.contains} {summary.areas} {app.areas}, {summary.actions} {settings.availableActions}, {summary.activityRecords} {settings.activities}, {summary.rewards} {settings.rewards} {language === 'es' ? 'y' : 'and'} {summary.rewardRedemptions} {settings.redemptions}.</p>
            <p>{settings.replaceHelp}</p>
            <div>
              <button className="button button--ghost" type="button" disabled={saving} onClick={() => setPendingBackup(null)}>{common.cancel}</button>
              <button className="button button--danger" type="button" disabled={saving} onClick={() => void restoreBackup()}>{saving ? settings.restoring : settings.replace}</button>
            </div>
          </section>
        )}
        <p className="app-version">{APP_RELEASE_LABEL}</p>
      </div>
    </dialog>
  )
}
